import React, { useEffect, useState } from "react";

interface Stage {
  name: string;
  status: "pending" | "in_progress" | "done" | "failed";
}

const initialStages: Stage[] = [
  { name: "Clone repository", status: "pending" },
  { name: "Yarn install", status: "pending" },
  { name: "Yarn Build", status: "pending" },
  { name: "Run tests", status: "pending" },
  { name: "Deploy to server", status: "pending" }
];

const JenkinsApp: React.FC = () => {
  const [stages, setStages] = useState<Stage[]>(initialStages);
  const [currentStage, setCurrentStage] = useState<number>(0);
  const [stopped, setStopped] = useState(false);

  useEffect(() => {
    if (currentStage >= stages.length || stopped) return;

    const progressStage = () => {
      setStages((prev) => {
        const updated = [...prev];
        if (currentStage > 0 && updated[currentStage - 1].status === "in_progress") {
          updated[currentStage - 1].status = "done";
        }
        updated[currentStage].status = "in_progress";
        return updated;
      });

      const randomDelay = Math.floor(Math.random() * 2000) + 1000; // 1–3s delay
      const willFail = Math.random() < 0.25; // ~15% chance of failure

      setTimeout(() => {
        setStages((prev) => {
          const updated = [...prev];
          updated[currentStage].status = willFail ? "failed" : "done";
          return updated;
        });

        if (willFail) {
          setStopped(true); // stop further processing
        } else {
          setCurrentStage((prev) => prev + 1); // proceed to next stage
        }
      }, randomDelay);
    };

    const initialDelay = setTimeout(progressStage, 600); // slight delay before starting

    return () => clearTimeout(initialDelay);
  }, [currentStage, stopped, stages.length]);

  const getStatusIcon = (status: Stage["status"]) => {
    switch (status) {
      case "done": return "✅";
      case "in_progress": return "🔄";
      case "pending": return "⬜";
      case "failed": return "❌";
    }
  };

  const restartPipeline = () => {
    setStages(initialStages.map(stage => ({ ...stage, status: "pending" })));
    setCurrentStage(0);
    setStopped(false);
  };

  return (
    <div className="p-6 font-mono text-black space-y-3 bg-white rounded-lg shadow-lg max-w-md mx-auto mt-6">
      <h2 className="text-xl font-bold text-gray-800">🤖 Jenkins Build Pipeline</h2>
      {stages.map((stage, index) => (
        <div key={index} className="flex items-center space-x-2">
          <span>{getStatusIcon(stage.status)}</span>
          <span className={
            stage.status === "in_progress"
              ? "text-blue-600 font-semibold"
              : stage.status === "done"
              ? "text-green-700"
              : stage.status === "failed"
              ? "text-red-600 font-semibold"
              : "text-gray-500"
          }>
            {stage.name}
          </span>
        </div>
      ))}


      {stopped && (
        <div className="mt-4 text-red-700 font-semibold">
          ❌ Build failed. Fix the error and try again.
        </div>
      )}

      {stopped && (
        <button
          onClick={restartPipeline}
          className="mt-2 px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
        >
          Restart Build
        </button>
      )}

      {!stopped && currentStage >= stages.length && (
        <div className="mt-4 text-green-700 font-semibold text-sm whitespace-pre-wrap">
          ✅ All stages passed. No red lights.{"\n"}
          Looks like I know my way around Jenkins.{"\n"}
          CI/CD? Consider it... Deployed. 🚀
        </div>
      )}
          </div>
        );
};

export default JenkinsApp;
